import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Collapse,
    Divider,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from "@mui/material";
import { HttpOrder } from "../../http";
import { Order, OrderDetail, OrderFilter, PaginatedOrders } from "../../types/profileOrder/Order";
import { BUTTON_STYLES } from "../../styles/buttonStyles";
import { TYPOGRAPHY_STYLES } from "../../styles/typographyStyles";
import { PaginationControls } from "../Pagination/PaginationControls"
import RoomOutlinedIcon from "@mui/icons-material/RoomOutlined";
import { SHIPPING_ACTION_LINK_SX } from "../../styles/shippingActionLinkStyles";
import { mapsUrlBuilder } from "@ecommerce/shared-utils";



type DetailState =
    | { status: "idle" }
    | { status: "loading" }
    | { status: "error"; error: string }
    | { status: "loaded"; data: OrderDetail };

const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("es-AR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    });
};

const formatPrice = (price: number) =>
    new Intl.NumberFormat("es-AR", { style: "currency", currency: "ARS" }).format(price);

const getStatusColor = (estadoCod: number) => {
    switch (estadoCod) {
        case 1:
            return "default";
        case 2:
            return "warning";
        case 3:
            return "success";
        case 4:
            return "error";
        default:
            return "default";
    }
};

const getShippingStatusColor = (estadoEnvioCod?: number) => {
    switch (estadoEnvioCod) {
        case 1:
            return "default";
        case 2:
            return "info";
        case 4:
            return "success";
        default:
            return "default";
    }
};

const buildVariantDesc = (detail: OrderDetail, idProductoVariante: string) => {
    const attrs = (detail.atributosVariante ?? [])
        .filter((a) => a.idProductoVariante === idProductoVariante)
        .map((a) => a.nombreAtributoValor)
        .filter(Boolean);

    return attrs.length ? attrs.join(", ") : "";
};

/** Tipografías de esta pantalla: todo deriva de TYPOGRAPHY_STYLES */
const TEXT = {
    pageTitle: {
        ...TYPOGRAPHY_STYLES.rubik,
        fontSize: 24,
        fontWeight: 700,
    },
    cardTitle: {
        ...TYPOGRAPHY_STYLES.rubikLarge,
        fontWeight: 700,
    },
    meta: {
        ...TYPOGRAPHY_STYLES.rubikSmall,
        opacity: 0.8,
    },
    sectionTitle: {
        ...TYPOGRAPHY_STYLES.rubikLarge,
        fontWeight: 800,
    },
    label: {
        ...TYPOGRAPHY_STYLES.rubikSmall,
        color: "text.secondary",
    },
    value: {
        ...TYPOGRAPHY_STYLES.rubikMedium,
        fontWeight: 600,
    },
    valueStrong: {
        ...TYPOGRAPHY_STYLES.rubikLarge,
        fontWeight: 900,
    },
    valueStrongGrey: {
        ...TYPOGRAPHY_STYLES.rubikLarge,
        fontWeight: 900,
        color: BUTTON_STYLES.primary.color,
    },
    tableHeader: {
        ...TYPOGRAPHY_STYLES.rubikMedium,
        fontWeight: 700,
    },
    tableName: {
        ...TYPOGRAPHY_STYLES.rubikMedium,
        fontWeight: 700,
    },
    tableText: {
        ...TYPOGRAPHY_STYLES.rubikMedium,
    },
    tableStrong: {
        ...TYPOGRAPHY_STYLES.rubikMedium,
        fontWeight: 800,
    },
    variant: {
        ...TYPOGRAPHY_STYLES.rubikSmall,
        opacity: 0.75,
    },
    showingLabel: {
        ...TYPOGRAPHY_STYLES.rubikMedium,
        fontWeight: 600,
    },
} as const;

export const ProfileOrdersSection = () => {
    const [orders, setOrders] = useState<Order[]>([]);
    const [loadingList, setLoadingList] = useState(false);

    const [pagination, setPagination] = useState({
        cantPages: 0,
        actualPage: 1,
        cantRecords: 0,
        pageSize: 20,
    });

    const [filter, setFilter] = useState<OrderFilter>({
        pagina: 1,
        tamanoPagina: 20,
        estadoOrden: null,
        estadoEnvio: null,
    });

    const [expandedId, setExpandedId] = useState<string | null>(null);
    const [detailsById, setDetailsById] = useState<Record<string, DetailState>>({});

    const loadOrders = useCallback(async () => {
        setLoadingList(true);
        try {
            const response: PaginatedOrders = await HttpOrder.getAllOrders(filter);
            setOrders(response.paginaOrdenesDTO.lista);
            setPagination(response.paginaOrdenesDTO.paginacion);
            setExpandedId(null);
        } catch (e: any) {
            console.error("Error loading profile orders:", e);
            setOrders([]);
            setPagination({
                cantPages: 0,
                actualPage: filter.pagina,
                cantRecords: 0,
                pageSize: filter.tamanoPagina,
            });
        } finally {
            setLoadingList(false);
        }
    }, [filter]);

    useEffect(() => {
        loadOrders();
    }, [loadOrders]);

    const handlePageChange = (_: React.ChangeEvent<unknown>, page: number) => {
        setFilter((prev) => ({ ...prev, pagina: page }));
    };

    const handlePageSizeChange = (event: any) => {
        const newPageSize = event.target.value;
        setFilter((prev) => ({ ...prev, tamanoPagina: newPageSize, pagina: 1 }));
    };

    const showingLabel = useMemo(() => {
        if (pagination.cantRecords <= 0) return "No hay órdenes para mostrar";
        const from = (pagination.actualPage - 1) * pagination.pageSize + 1;
        const to = Math.min(pagination.actualPage * pagination.pageSize, pagination.cantRecords);
        return `Mostrando ${from}-${to} ordenes de ${pagination.cantRecords}`;
    }, [pagination]);

    const ensureDetailLoaded = async (orderId: string) => {
        const current = detailsById[orderId];
        if (current?.status === "loaded" || current?.status === "loading") return;

        setDetailsById((prev) => ({ ...prev, [orderId]: { status: "loading" } }));
        try {
            const detail = await HttpOrder.getOrderById(orderId);
            setDetailsById((prev) => ({ ...prev, [orderId]: { status: "loaded", data: detail } }));
        } catch (e: any) {
            setDetailsById((prev) => ({
                ...prev,
                [orderId]: { status: "error", error: e?.message ?? "Error al cargar el detalle" },
            }));
        }
    };

    const toggleExpand = async (orderId: string) => {
        const next = expandedId === orderId ? null : orderId;
        setExpandedId(next);

        if (next) {
            await ensureDetailLoaded(orderId);
        }
    };

    const renderOrderSummary = (o: Order) => {
        const shippingNet = Math.max(0, (o.costoEnvio ?? 0) - (o.descuentosCostoEnvio ?? 0));
        const shippingText = shippingNet <= 0 ? "GRATIS" : formatPrice(shippingNet);

        return (
            <Box sx={{ width: "100%" }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, alignItems: "flex-start" }}>
                    <Box>
                        <Typography sx={TEXT.cardTitle}>Orden #{o.codigo ?? o.bigIdOrden}</Typography>
                        <Typography sx={TEXT.meta}>{formatDate(o.fechaAlta)}</Typography>
                    </Box>

                    {/*Descomentar para ver estados de orden*/} 

                    {/*<Stack direction="row" spacing={1} sx={{ alignItems: "center", flexWrap: "wrap" }}>*/}
                    {/*    <Chip label={o.estadoDescripcion} color={getStatusColor(o.codOrdenEstado)} size="small" />*/}

                    {/*    {o.codOrdenEstado !== 1 && o.codOrdenEstadoEnvio ? (*/}
                    {/*        <Chip*/}
                    {/*            label={o.estadoEnvioDescripcion ?? "-"}*/}
                    {/*            color={getShippingStatusColor(o.codOrdenEstadoEnvio)}*/}
                    {/*            size="small"*/}
                    {/*        />*/}
                    {/*    ) : null}*/}
                    {/*</Stack>*/}
                </Box>

                <Divider sx={{ my: 2 }} />

                <Box
                    sx={{
                        display: "grid",
                        gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                        gap: 1.5,
                    }}
                >
                    <Box>
                        <Typography sx={TEXT.label}>Total</Typography>
                        <Typography sx={TEXT.valueStrongGrey}>{formatPrice(o.total)}</Typography>
                    </Box>

                    <Box>
                        <Typography sx={TEXT.label}>Cantidad de productos</Typography>
                        <Typography sx={TEXT.value}>{o.cantidadProductos}</Typography>
                    </Box>

                    <Box>
                        <Typography sx={TEXT.label}>Subtotal (sin envío)</Typography>
                        <Typography sx={TEXT.value}>{formatPrice(o.subTotal)}</Typography>
                    </Box>

                    <Box>
                        <Typography sx={TEXT.label}>Precio de envío</Typography>
                        <Typography sx={TEXT.value}>{shippingText}</Typography>
                    </Box>
                </Box>

                <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-end" }}>
                    <Button
                        size="small"
                        variant="contained"
                        onClick={() => toggleExpand(o.id)}
                        sx={{
                            ...(expandedId === o.id ? BUTTON_STYLES.invertedPrimary : BUTTON_STYLES.primary),
                            ...BUTTON_STYLES.compact,
                            width: "auto",
                            minWidth: "fit-content",
                            px: 2.5,
                            height: "32px",
                            justifyContent: "center",
                        }}
                    >
                        {expandedId === o.id ? "Ocultar detalle" : "Ver detalle"}
                    </Button>

                </Box>
            </Box>
        );
    };

    const renderOrderDetail = (orderId: string) => {
        const state = detailsById[orderId] ?? { status: "idle" };

        if (state.status === "loading") {
            return (
                <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                    <CircularProgress size={26} />
                </Box>
            );
        }

        if (state.status === "error") {
            return (
                <Alert
                    severity="error"
                    sx={{ mt: 2 }}
                    action={
                        <Button
                            color="inherit"
                            size="small"
                            onClick={() => {
                                setDetailsById((prev) => ({ ...prev, [orderId]: { status: "idle" } }));
                                ensureDetailLoaded(orderId);
                            }}
                        >
                            Reintentar
                        </Button>
                    }
                >
                    {state.error}
                </Alert>
            );
        }

        if (state.status !== "loaded") return null;

        const detail = state.data;
        const o = detail.orden;

        const domicilioEntregaText = (() => {
            const d = detail.domicilioEntrega;
            if (!d) return "-";
            const piso = d.pisoDpto ? ` (${d.pisoDpto})` : "";
            const loc = d.descLocalidad ? `, ${d.descLocalidad}` : "";
            const cp = d.codigoPostal ? `, CP ${d.codigoPostal}` : "";
            return `${d.calle ?? ""} ${d.calleNro ?? ""}${piso}${loc}${cp}`.trim() || "-";
        })();

        const branch = detail.sucursal;
        const hasBranch = Boolean(branch);
        const branchMapsUrl = mapsUrlBuilder.buildBranchUrl(o.nombreProveedorEnvio, branch);


        return (
            <Box sx={{ mt: 2 }}>
                <Divider sx={{ mb: 2 }} />

                <Stack direction={{ xs: "column", md: "row" }} spacing={3} sx={{ mb: 2 }}>
                    <Box sx={{ flex: 1 }}>
                        <Typography sx={{ ...TEXT.sectionTitle, mb: 1 }}>Información del cliente</Typography>

                        <Typography sx={TEXT.label}>Nombre</Typography>
                        <Typography sx={{ ...TEXT.value, mb: 1 }}>{o.nombreCliente}</Typography>

                        <Typography sx={TEXT.label}>Email</Typography>
                        <Typography sx={{ ...TEXT.value, mb: 1 }}>{o.emailCliente}</Typography>

                        <Typography sx={TEXT.label}>Documento</Typography>
                        <Typography sx={{ ...TEXT.value, mb: 1 }}>{o.documento ?? "-"}</Typography>

                        <Typography sx={TEXT.label}>Teléfono</Typography>
                        <Typography sx={TEXT.value}>{o.telefono ?? "-"}</Typography>
                    </Box>

                    <Box sx={{ flex: 1 }}>
                        <Typography sx={{ ...TEXT.sectionTitle, mb: 1 }}>Información de envío</Typography>

                        <Typography sx={TEXT.label}>Tipo</Typography>
                        <Typography sx={{ ...TEXT.value, mb: 1 }}>
                            {(o.nombreProveedorEnvio ?? "").trim() || "-"}
                            {o.descEnvioOpcion ? ` - ${o.descEnvioOpcion}` : ""}
                        </Typography>

                        {/* Si hay sucursal */}
                        {hasBranch ? (
                            <>
                                <Typography sx={TEXT.label}>Sucursal de entrega</Typography>

                                <Box sx={{ mt: 0.5 }}>
                                    <Typography sx={{ ...TEXT.value, mb: 0.5 }}>
                                        {branch?.nombre ?? "Sucursal"}
                                        {branch?.codigoPostal ? ` (${branch.codigoPostal})` : ""}
                                    </Typography>

                                    <Stack
                                        direction={{ xs: "column", sm: "row" }}
                                        spacing={1}
                                        sx={{ alignItems: { sm: "center" }, flexWrap: "wrap" }}
                                    >
                                        <Typography sx={TEXT.value}>
                                            {branch?.direccion ?? "-"}
                                        </Typography>

                                        {branchMapsUrl && (
                                            <Button
                                                component="a"
                                                href={branchMapsUrl}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                startIcon={<RoomOutlinedIcon sx={{ fontSize: 16 }} />}
                                                sx={SHIPPING_ACTION_LINK_SX}
                                            >
                                                Ver en mapa
                                            </Button>
                                        )}
                                    </Stack>

                                    {(branch?.telefonoContacto || branch?.emailContacto) ? (
                                        <Box sx={{ mt: 1 }}>

                                            {!!branch?.telefonoContacto && (
                                                <>
                                                    <Typography sx={TEXT.label}>Teléfono sucursal</Typography>
                                                    <Typography sx={{ ...TEXT.value, mb: 0.75 }}>
                                                        {branch.telefonoContacto}
                                                    </Typography>
                                                </>
                                            )}

                                            {!!branch?.emailContacto?.trim() && (
                                                <>
                                                    <Typography sx={TEXT.label}>Email sucursal</Typography>
                                                    <Typography sx={TEXT.value}>
                                                        {branch.emailContacto}
                                                    </Typography>
                                                </>
                                            )}
                                        </Box>
                                    ) : null}
                                </Box>
                            </>
                        ) : o.solicitaDomicilioEnvio ? (
                            /* Si NO hay sucursal y solicita domicilio => mostrar domicilio */
                            <>
                                <Typography sx={TEXT.label}>Domicilio de entrega</Typography>
                                <Typography sx={{ ...TEXT.value, mb: 1 }}>
                                    {domicilioEntregaText}
                                </Typography>
                            </>
                        ) : (
                            /* Si NO hay sucursal y NO solicita domicilio => mensaje */
                            <Typography sx={{ ...TEXT.meta, mt: 1 }}>
                                Este tipo de envío no requiere domicilio ni sucursal.
                            </Typography>
                        )}
                    </Box>

                </Stack>

                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, borderColor: "#e0e0e0", mb: 2 }}>
                    <Typography sx={{ ...TEXT.sectionTitle, mb: 1 }}>Productos</Typography>

                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow sx={{ backgroundColor: "grey.50" }}>
                                    <TableCell sx={TEXT.tableHeader}>Producto</TableCell>
                                    <TableCell sx={TEXT.tableHeader} align="center">
                                        Cant.
                                    </TableCell>
                                    <TableCell sx={TEXT.tableHeader} align="right">
                                        Precio
                                    </TableCell>
                                    <TableCell sx={TEXT.tableHeader} align="right">
                                        Subtotal
                                    </TableCell>
                                </TableRow>
                            </TableHead>

                            <TableBody>
                                {detail.productos.map((p) => {
                                    const variante = buildVariantDesc(detail, p.idProductoVariante);
                                    return (
                                        <TableRow key={`${p.idProducto}-${p.idProductoVariante}`}>
                                            <TableCell>
                                                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                                                    {p.urlImagen ? (
                                                        <img
                                                            src={p.urlImagen}
                                                            alt={p.nombre}
                                                            style={{ width: 44, height: 44, objectFit: "cover", borderRadius: 6 }}
                                                        />
                                                    ) : null}

                                                    <Box>
                                                        <Typography sx={TEXT.tableName}>{p.nombre}</Typography>
                                                        {variante ? <Typography sx={TEXT.variant}>({variante})</Typography> : null}
                                                    </Box>
                                                </Box>
                                            </TableCell>

                                            <TableCell align="center">
                                                <Typography sx={TEXT.tableText}>{p.cantidad}</Typography>
                                            </TableCell>

                                            <TableCell align="right">
                                                <Typography sx={TEXT.tableText}>{formatPrice(p.precio)}</Typography>
                                            </TableCell>

                                            <TableCell align="right">
                                                <Typography sx={TEXT.tableStrong}>{formatPrice(p.subtotal)}</Typography>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>

                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, borderColor: "#e0e0e0" }}>
                    <Typography sx={{ ...TEXT.sectionTitle, mb: 1 }}>Resumen</Typography>

                    <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                        <Typography sx={TEXT.tableText}>Subtotal</Typography>
                        <Typography sx={{ ...TEXT.tableText, fontWeight: 700 }}>{formatPrice(o.subTotal)}</Typography>
                    </Box>

                    <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                        <Typography sx={TEXT.tableText}>Costo de envío</Typography>
                        <Typography sx={{ ...TEXT.tableText, fontWeight: 700 }}>{formatPrice(o.costoEnvio)}</Typography>
                    </Box>

                    {o.descuentos > 0 ? (
                        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                            <Typography sx={TEXT.tableText}>Descuentos</Typography>
                            <Typography sx={{ ...TEXT.tableText, fontWeight: 700 }} color="error.main">
                                -{formatPrice(o.descuentos)}
                            </Typography>
                        </Box>
                    ) : null}

                    {o.descuentosCostoEnvio > 0 && o.descuentosCostoEnvio !== o.costoEnvio ? (
                        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                            <Typography sx={TEXT.tableText}>Descuento costo envío</Typography>
                            <Typography sx={{ ...TEXT.tableText, fontWeight: 700 }} color="error.main">
                                -{formatPrice(o.descuentosCostoEnvio)}
                            </Typography>
                        </Box>
                    ) : null}

                    <Divider sx={{ my: 1.5 }} />

                    <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                        <Typography sx={TEXT.valueStrong}>Total</Typography>
                        <Typography sx={TEXT.valueStrongGrey}>{formatPrice(o.total)}</Typography>
                    </Box>
                </Paper>
            </Box>
        );
    };

    return (
        <Box>
            <Typography sx={{ ...TEXT.pageTitle, mb: 3 }}>Ordenes</Typography>

            {loadingList ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                    <CircularProgress />
                </Box>
            ) : orders.length === 0 ? (
                <Typography sx={TEXT.value}>No tenés ordenes para mostrar.</Typography>
            ) : (
                <Box>
                    {orders.map((o) => (
                        <Paper
                            key={o.id}
                            sx={{
                                p: 2,
                                mb: 2,
                                border: "1px solid #ddd",
                                borderRadius: 2,
                                transition: "0.2s",
                                "&:hover": { borderColor: "#000", backgroundColor: "#fafafa" },
                            }}
                        >
                            {renderOrderSummary(o)}

                            <Collapse in={expandedId === o.id} timeout="auto" unmountOnExit>
                                {renderOrderDetail(o.id)}
                            </Collapse>
                        </Paper>
                    ))}
                </Box>
            )}

            {pagination.cantPages > 0 ? (
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mt: 3,
                        flexWrap: "wrap",
                        gap: 2,
                    }}
                >
                    <PaginationControls
                        showingLabel={showingLabel}
                        page={pagination.actualPage}
                        pageCount={pagination.cantPages}
                        pageSize={filter.tamanoPagina}
                        onPageChange={(page) => setFilter((prev) => ({ ...prev, pagina: page }))}
                        onPageSizeChange={(size) => setFilter((prev) => ({ ...prev, tamanoPagina: size, pagina: 1 }))}
                        disabled={loadingList}
                    />

                </Box>
            ) : null}
        </Box>
    );
};














